import { useCallback, useEffect, useState } from 'react';
import { quotesByQuery, quoteByCode, quoteImages, uploadQuoteImages, opsErr, okCode } from '../../api/ops';
import { useToast } from '../../components/Toast';
import { OpsGate, useOps, ScanSearch, DateRange, PhotoPicker, QuoteImages, QuoteDetail, IMAGE_TYPES, daysAgo, toInputDate, inputToDmy } from './opsCommon';

// CẬP NHẬT ẢNH ĐƠN HÀNG (app "i love GTS"): tìm/quét đơn -> chọn loại ảnh (kho nước ngoài / kho VN / sale)
// -> chụp/chọn ảnh -> gửi. Bên dưới là lịch sử cập nhật ảnh (lọc loại + khoảng ngày).
const typeLabel = (k) => (IMAGE_TYPES.find((t) => t.key === k) || {}).label || k;

function Upload({ quote: initial, onBack }) {
  const account = useOps();
  const toast = useToast();
  const [quote, setQuote] = useState(initial);
  // SocSon nhận type_image dạng CHỈ SỐ "0" | "1" | "2" (theo thứ tự IMAGE_TYPES) — đúng như app gốc gửi.
  const [type, setType] = useState('0');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const d = await uploadQuoteImages(quote.code, type, files.map((f) => f.file));
      if (!okCode(d)) return toast(d?.message || 'Cập nhật ảnh không thành công', 'danger');
      toast('Cập nhật ảnh thành công');
      setFiles([]);
      const r = await quotesByQuery(quote.code).catch(() => null);
      const fresh = r?.data?.results?.[0];
      if (fresh) setQuote(fresh);
    } catch (e) {
      toast(opsErr(e, 'Cập nhật ảnh không thành công'), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn sm ghost" style={{ marginBottom: 10 }} onClick={onBack}><i className="ti ti-arrow-left" /> Quay lại</button>
      <div className="card">
        <div className="ops-kv">
          <div><span>Mã đơn hàng</span><b>{quote.code}</b></div>
          {quote.tracking_no && <div><span>Mã tracking</span><b>{quote.tracking_no}</b></div>}
          {quote.name && <div><span>Sản phẩm</span><b>{quote.name}</b></div>}
          {quote.note && <div><span>Ghi chú</span><b>{quote.note}</b></div>}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <label className="small">Loại ảnh</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {IMAGE_TYPES.map((t, i) => <option key={t.key} value={String(i)}>{t.label}</option>)}
          </select>
        </div>
        <PhotoPicker files={files} setFiles={setFiles} />
        <button className="btn" style={{ width: '100%', marginTop: 12 }} disabled={!files.length || busy} onClick={submit}>
          {busy ? 'Đang gửi…' : `Xác nhận (${files.length} ảnh)`}
        </button>
      </div>
      <div className="card" style={{ marginTop: 12 }}>
        <h3><i className="ti ti-photo" /> Hình ảnh đã cập nhật</h3>
        <QuoteImages quote={quote} base={account.base} />
      </div>
    </>
  );
}

function History({ onOpen }) {
  const toast = useToast();
  const [type, setType] = useState(''); // '' = tất cả
  const [from, setFrom] = useState(toInputDate(daysAgo(7)));
  const [to, setTo] = useState(toInputDate(new Date()));
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const params = { from: inputToDmy(from), to: inputToDmy(to), size: 20, page: p };
      if (type) params.type_image = type;
      const d = await quoteImages(params);
      const res = okCode(d) ? d.data?.results || [] : [];
      // Gộp theo (ngày tải + mã tracking) như app gốc.
      setRows((prev) => {
        const map = new Map(p > 1 ? prev.map((r) => [r.key, r]) : []);
        res.forEach((im) => {
          const date = String(im.upload_date || '').split(/[ T]/)[0];
          const key = `${date}-${im.quote?.tracking_no || ''}`;
          if (!map.has(key)) map.set(key, { key, date, tracking: im.quote?.tracking_no || '', name: im.quote?.name || '', type: im.type_image, customer: im.user?.full_name || '' });
        });
        return [...map.values()];
      });
      setPage(p);
      setMore(res.length >= 20);
    } catch (e) {
      if (p === 1) setRows([]);
      // SocSon/PMBox trả LỖI (4xx "Không tìm thấy ảnh!") khi CHƯA có ảnh nào trong khoảng ngày — đó là
      // danh sách RỖNG chứ không phải lỗi thật. Chỉ hiện "Chưa có lịch sử.", KHÔNG bung toast đỏ làm NV hoảng.
      const status = e?.response?.status;
      const msg = opsErr(e, '');
      const emptyResult = status === 404 || /tìm thấy ảnh|không có ảnh|no image|not found/i.test(msg);
      if (!emptyResult) toast(opsErr(e, 'Không tải được lịch sử ảnh'), 'danger');
    } finally {
      setLoading(false);
    }
  }, [from, to, type, toast]);
  useEffect(() => { load(1); }, [load]);

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3><i className="ti ti-history" /> Lịch sử cập nhật ảnh</h3>
      <div className="row" style={{ marginBottom: 10 }}>
        <select value={type} onChange={(e) => setType(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Tất cả hình ảnh</option>
          {IMAGE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      </div>
      {!rows.length && !loading ? <div className="empty">Chưa có lịch sử.</div> : (
        <div className="ops-list">
          {rows.map((r) => (
            <button key={r.key} type="button" className="ops-hist" onClick={() => onOpen(r.tracking)}>
              <div className="row between"><b>{r.tracking}</b><span className="small muted">{r.date}</span></div>
              <div className="small">{r.name}</div>
              <div className="small muted">{typeLabel(r.type)}{r.customer ? ` · KH: ${r.customer}` : ''}</div>
            </button>
          ))}
        </div>
      )}
      {loading && <div className="empty">Đang tải…</div>}
      {more && !loading && <button className="btn sm ghost" style={{ width: '100%', marginTop: 8 }} onClick={() => load(page + 1)}>Tải thêm</button>}
    </div>
  );
}

function Images() {
  const account = useOps();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState(null); // nhiều đơn khớp -> chọn 1
  const [target, setTarget] = useState(null); // đơn đang cập nhật ảnh
  const [detail, setDetail] = useState(null); // xem chi tiết từ lịch sử

  const search = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const d = await quotesByQuery(code);
      const list = okCode(d) ? d.data?.results || [] : [];
      if (!list.length) toast('Đơn hàng không tồn tại', 'danger');
      else if (list.length === 1) setTarget(list[0]);
      else setChoices(list);
    } catch (e) {
      toast(opsErr(e, 'Không tìm thấy đơn hàng'), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (tracking) => {
    try {
      const d = await quoteByCode(tracking);
      if (okCode(d) && d.data) setDetail(d.data);
      else toast('Không tìm thấy đơn hàng', 'danger');
    } catch (e) {
      toast(opsErr(e, 'Không tìm thấy đơn hàng'), 'danger');
    }
  };

  if (target) return <Upload quote={target} onBack={() => { setTarget(null); setChoices(null); }} />;
  if (detail) return (
    <>
      <button className="btn sm ghost" style={{ marginBottom: 10 }} onClick={() => setDetail(null)}><i className="ti ti-arrow-left" /> Quay lại</button>
      <QuoteDetail quote={detail} base={account.base} />
    </>
  );

  return (
    <>
      <ScanSearch value={q} onChange={setQ} onSubmit={search} busy={busy} placeholder="Mã đơn / mã tracking…" />
      {choices && (
        <div className="card">
          <h3>Chọn đơn hàng ({choices.length})</h3>
          <div className="ops-list">
            {choices.map((c) => (
              <button key={c._id || c.code} type="button" className="ops-hist" onClick={() => setTarget(c)}>
                <b>{c.code}</b>
                <div className="small muted">{c.tracking_no} {c.name ? `· ${c.name}` : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      <History onOpen={openHistory} />
    </>
  );
}

export default function OpsImagesPage({ onBack }) {
  return (
    <OpsGate onBack={onBack} title="Cập nhật ảnh đơn hàng" sub="Chụp ảnh kho nước ngoài / kho Việt Nam / sale cho đơn">
      <Images />
    </OpsGate>
  );
}
